import { DeviceTransferStatus } from "@/gql/graphql";
import { Stack } from "@mui/material";
import { Card, Icon, Tooltip, Typography } from "@trimble/modus-cmx";
import Badge from "@/components/Badge";
import { useTranslation } from "react-i18next";
import ExternalBadge from "./Badges/ExternalBadge";
import DeviceIcon from "./DeviceIcon";
import RemoveIconButton from "@/components/ui/RemoveIconButton";
import OfflineLicenseDetails from "./OfflineLicenseDetails";

type OfflineLicenseInfo = {
  id: string;
  checkOutDate: string;
  checkInDate: string;
  revokeWhenOnline: boolean;
};

type DeviceItemProps = {
  device: {
    id: string;
    description?: string | null;
    iconUrl?: string | null;
    isExternal: boolean;
    modelName?: string | null;
    serialNumber: string;
    transferStatus?: DeviceTransferStatus | null;
    desiredDealerCustomer?: { companyName: string } | null;
    owner?: { __typename?: string; companyName: string } | null;
  };
  canRemove?: boolean;
  removeDisabled?: boolean;
  hideCompanyName?: boolean;
  hideExternalBadge?: boolean;
  onRemove?: () => void;
  superKeyLinesCount?: number;
  offlineLicense?: OfflineLicenseInfo | null;
  customerAccountId?: string;
};

const DeviceItem = (props: DeviceItemProps) => {
  const {
    device,
    canRemove,
    removeDisabled,
    hideCompanyName,
    hideExternalBadge,
    onRemove,
    superKeyLinesCount,
    offlineLicense,
    customerAccountId,
  } = props;
  const { t } = useTranslation();

  const ownerBadgeColor = (() => {
    if (device.owner?.__typename !== "Account") return "success";
    if (device.transferStatus === DeviceTransferStatus.Failed) return "danger";
    return "high-contrast";
  })();

  return (
    <Card bordered data-testid={`device-item-${device.id}`}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <DeviceIcon iconUrl={device.iconUrl} />
        <Stack>
          <Stack direction="row" spacing={1}>
            <Typography weight="semibold">{device.modelName}</Typography>
            <Badge color="tertiary">
              <Icon name="file_type_numbers" size="xs" />
              {device.serialNumber}
            </Badge>
            {device.isExternal && !hideExternalBadge && <ExternalBadge />}
            {(superKeyLinesCount ?? 0) > 0 && (
              <Tooltip
                content={t(
                  "contract_summary.subscription.assigned_superkey_count",
                  {
                    count: superKeyLinesCount,
                  }
                )}
              >
                <Badge variant="outlined" color="primary">
                  <Icon name="key" size="xs" variant="solid" />
                  {t("devices.superkey")}
                </Badge>
              </Tooltip>
            )}
          </Stack>
          <Typography
            size="sm"
            style={{ color: "var(--modus-wc-color-gray-6)" }}
          >
            {device.description}
          </Typography>
        </Stack>
        <Stack flexGrow={1} />
        <Stack direction="row" alignItems="center" spacing={3}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {device.transferStatus === DeviceTransferStatus.Failed && (
              <Tooltip
                content={t(
                  "contract_summary.subscription.device_transfer_status_failed"
                )}
              >
                <Icon
                  name="alert"
                  size="xs"
                  variant="solid"
                  style={{ color: "var(--modus-wc-color-error)" }}
                />
              </Tooltip>
            )}
            {device.owner && !hideCompanyName && (
              <Tooltip
                content={
                  device.transferStatus === DeviceTransferStatus.Scheduled
                    ? t("devices.transfer_scheduled", {
                        customerName:
                          device.desiredDealerCustomer?.companyName ??
                          t("unknown"),
                      })
                    : t("devices.owner")
                }
              >
                <Badge color={ownerBadgeColor} variant="outlined">
                  {device.owner.companyName}
                  {device.transferStatus && <Icon name="calendar" size="xs" />}
                </Badge>
              </Tooltip>
            )}
          </Stack>
          {canRemove && (
            <RemoveIconButton
              aria-label={t("aria.unassign_device")}
              disabled={
                device.transferStatus === DeviceTransferStatus.Failed ||
                removeDisabled
              }
              onButtonClick={onRemove}
            />
          )}
        </Stack>
      </Stack>
      {offlineLicense && customerAccountId && (
        <OfflineLicenseDetails
          licenseId={offlineLicense.id}
          customerAccountId={customerAccountId}
          checkOutDate={offlineLicense.checkOutDate}
          checkInDate={offlineLicense.checkInDate}
          revokeWhenOnline={offlineLicense.revokeWhenOnline}
          disabled={!canRemove || removeDisabled}
        />
      )}
    </Card>
  );
};

export default DeviceItem;
